import { Translations } from '../types/language';

/** Unicode bidi control chars Azure Translator may inject into any language output. */
const BIDI_CONTROL_CHARS = /[\u200E\u200F\u202A-\u202E\u2066-\u2069]/g;

export function sanitizeTranslationText(text: string): string {
	return text.replace(BIDI_CONTROL_CHARS, '');
}

export function sanitizeTranslations(translations: Translations): Translations {
	const sanitized: Translations = {};
	for (const [key, value] of Object.entries(translations)) {
		sanitized[key] = sanitizeTranslationText(value);
	}
	return sanitized;
}

/**
 * Minimum number of distinct English inputs that must collapse to a single output before
 * we treat the output as corrupt. Kept high enough that legitimate shared translations
 * (e.g. "Scan Barcode" and "Scan Bill" both → "I-scan ang Barcode") are never touched.
 */
const AUTO_TRANSLATE_COLLISION_THRESHOLD = 3;

/**
 * Azure Translator's model for some languages (notably Filipino/`fil`) intermittently
 * returns the SAME wrong string for many unrelated inputs — e.g. "Printed Receipt",
 * "Check Payment", "Debit Card", etc. all come back as "Email Address *". Detect that
 * signature (one output shared by several DISTINCT source strings) and fall back to the
 * English source for the affected keys, so the UI shows readable English instead of a
 * wrong, misleading label. `source` is the English input sent to the translator;
 * `translated` is the (already sanitized) response.
 */
export function dropCollidingAutoTranslations(
	source: Translations,
	translated: Translations
): { cleaned: Translations; revertedKeys: string[] } {
	const keysByOutput = new Map<string, string[]>();
	for (const [key, value] of Object.entries(translated)) {
		const normalized = value?.trim();
		if (!normalized) continue;
		const keys = keysByOutput.get(normalized) ?? [];
		keys.push(key);
		keysByOutput.set(normalized, keys);
	}

	const cleaned: Translations = { ...translated };
	const revertedKeys: string[] = [];
	for (const keys of keysByOutput.values()) {
		if (keys.length < AUTO_TRANSLATE_COLLISION_THRESHOLD) continue;
		// Legitimate shared translations come from identical English; corruption maps many
		// genuinely different English strings onto one output.
		const distinctSources = new Set(keys.map((key) => (source[key] ?? '').trim().toLowerCase()));
		if (distinctSources.size < AUTO_TRANSLATE_COLLISION_THRESHOLD) continue;
		for (const key of keys) {
			if (source[key]) {
				cleaned[key] = source[key];
				revertedKeys.push(key);
			}
		}
	}
	return { cleaned, revertedKeys };
}
